// Marketing layout primitives (DESIGN.md). The _site layout already provides the
// header, footer and dark theme, so pages compose these and nothing else.
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

/** A page section. `raised` is a full-width band on the raised surface. */
export function MSection({
  id,
  tone = 'default',
  className,
  children,
}: {
  id?: string
  tone?: 'default' | 'raised'
  className?: string
  children: ReactNode
}) {
  return (
    <section id={id} className={cn('scroll-mt-20', tone === 'raised' && 'border-y border-border bg-band', className)}>
      {/* data-reveal opts this band into the scroll reveal (src/lib/reveal.ts).
          The hidden state lives in CSS and only applies once an inline script
          has marked <html>, so this is inert if JS or the observer is missing. */}
      <div className="container mx-auto px-4 py-12 md:py-[72px]" data-reveal>
        {children}
      </div>
    </section>
  )
}

export function SectionHeader({
  title,
  lead,
  className,
  align = 'left',
}: {
  title: string
  lead?: ReactNode
  className?: string
  /** `center` for a header that sits above full-width content; `left` inside a split column. */
  align?: 'left' | 'center'
}) {
  return (
    <div className={cn('max-w-2xl', align === 'center' && 'mx-auto text-center', className)}>
      <h2 className="text-balance text-[1.75rem] font-semibold leading-tight tracking-tight md:text-headline">{title}</h2>
      {lead && <p className="mt-3 text-lead text-body">{lead}</p>}
    </div>
  )
}

/**
 * A band that is dark in both themes (the hero). The `dark` class gives the text
 * and buttons inside it dark-theme tokens; the page itself never forces a theme.
 */
export function DarkBand({ className, children }: { className?: string; children: ReactNode }) {
  return <section className={cn('dark relative isolate overflow-hidden bg-brand-dark text-foreground', className)}>{children}</section>
}

/**
 * Header on the left (5 of 12 columns), content on the right (7 of 12). Every
 * split section on a page uses this one split, so the right-hand blocks all start
 * on the same line. See DESIGN.md "Alignment".
 */
export function SplitSection({
  id,
  tone,
  title,
  lead,
  children,
}: {
  id?: string
  tone?: 'default' | 'raised'
  title: string
  lead?: ReactNode
  children: ReactNode
}) {
  return (
    <MSection id={id} tone={tone}>
      <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionHeader title={title} lead={lead} />
        </div>
        <div className="min-w-0 lg:col-span-7">{children}</div>
      </div>
    </MSection>
  )
}
